import React, {SyntheticEvent, useState} from 'react';
import intl from "react-intl-universal";
import {observer} from "mobx-react-lite";
import { useAppState, useRootStore, useUserState, useFileProvider, useDsManager } from "@stores/AppStateContext";
import { useCrudServices } from "../../../../platform/hooks/useServiceCombinations";

import {showInWorkspace} from "../../../../actions/ui/showInWorkspace";
import {moveToTrashUndoable, restoreFromTrashUndoable} from "../../../../actions/item/trash";
import {moveToArchiveUndoable, restoreFromArchiveUndoable} from "../../../../actions/item/archive";
import {pinItemUndoable, unPinItemUndoable} from "../../../../actions/item/pin";
import {logErrorToSentry} from "../../../../utils/errors/logErrorToSentry";
import {DocumentContextMenuComponent} from "../../../components/DocumentContextMenuComponent";

import {ArchiveIcon} from "../../../components/icons/ArchiveIcon";
import {UnarchiveIcon} from "../../../components/icons/UnarchiveIcon";
import {DeleteIcon} from "../../../components/icons/DeleteIcon";
import {DuplicateIcon} from "../../../components/icons/DuplicateIcon";
import {StarIcon} from "../../../components/icons/StarIcon";
import {StarIconFilled} from "../../../components/icons/StarIconFilled";
import {RestoreFromTrashIcon} from "../../../components/icons/RestoreFromTrashIcon";
import {JumpToElementIcon} from "../../../components/icons/JumpToElementIcon";
import {InfoIcon} from "../../../components/icons/InfoIcon";
import {DeleteForeverIcon} from "../../../components/icons/DeleteForeverIcon";
import {PrintIcon} from "../../../components/icons/PrintIcon";
import {DownloadIcon} from "../../../components/icons/DownloadIcon";

import {duplicateItemUndoable} from "../../../../actions/item/duplicateItem";
import {DocumentInfo} from "./DocumentInfo";
import {DocumentConfirmDelete} from "./DocumentConfirmDelete";

import { editorViewRef } from '../../../../ui/elements/EditorReact';


export interface ItemContextMenuProps {
    itemId: string,
    handleClose: () => void,
}

export const DocumentContextMenu = observer(({itemId, handleClose}: ItemContextMenuProps) => {
    const appState = useAppState();
    const rootStore = useRootStore();
    const userState = useUserState();
    const fileProvider = useFileProvider();
    const dsManager = useDsManager();
    const crudServices = useCrudServices();
    const [isInfoOpen, setInfoOpen] = useState(false);
    const _isReadOnly = userState?.isCurrentUserReadOnly
    let actions = [];
    const activeWorkspace = rootStore.view.workspace!;
    const item = activeWorkspace.items.get(itemId)!;
    const editorView = editorViewRef.current!;

    if (!item.trashed && !item.archived) {
        actions = [
            [
                {
                    text: intl.get('common.unpin').d('Unpin'),
                    Icon: StarIconFilled,
                    onClick: () => unPinItemUndoable(crudServices, dsManager!, itemId, activeWorkspace),
                    condition: !_isReadOnly && item.pinned
                },
                {
                    text: intl.get('common.pin_to_top').d('Pin to top'),
                    Icon: StarIcon,
                    onClick: () => pinItemUndoable(crudServices, dsManager!, itemId, activeWorkspace),
                    condition: !_isReadOnly && !item.pinned
                },

                {
                    text: intl.get('common.show_in_tree').d('Show in tree'),
                    Icon: JumpToElementIcon,
                    onClick: () => showInWorkspace(rootStore, itemId, activeWorkspace),
                },
            ],
            [
                {
                    text: intl.get('common.export').d('Export'),
                    Icon: DownloadIcon,
                    onClick: async () => {
                        const module = await import('../../../../actions/import/export/exportMarkdown');
                        module.exportMarkdownFile(item, editorView!, fileProvider);
                    },
                },
                {
                    text: intl.get('common.duplicate').d('Duplicate'),
                    Icon: DuplicateIcon,
                    onClick: () => {
                        if (!dsManager) {
                            console.error('Data source manager is not ready for duplicate action');
                            return;
                        }
                        duplicateItemUndoable(crudServices, {
                            workspace: activeWorkspace,
                            appState,
                            itemSource: item,
                            editorView,
                        }).catch(logErrorToSentry);
                    },
                    condition: !_isReadOnly
                },
                {
                    text: intl.get('common.archive_cap').d('Archive'),
                    Icon: ArchiveIcon,
                    onClick: () => moveToArchiveUndoable(crudServices, appState, itemId, activeWorkspace),
                    condition: !_isReadOnly
                },
                {
                    text: intl.get('common.print').d('Print'),
                    Icon: PrintIcon,
                    onClick: () => window.print(),
                    condition: !_isReadOnly
                },

            ],
            [
                {
                    text: intl.get('common.delete_cap').d('Delete'),
                    Icon: DeleteIcon,
                    onClick: () => moveToTrashUndoable(crudServices, appState, itemId, activeWorkspace),
                    condition: !_isReadOnly
                },
            ],
            [
                {
                    text: intl.get('common.info').d('Info'),
                    Icon: InfoIcon,
                    onClick: (event: SyntheticEvent) => {
                        event.stopPropagation();
                        setInfoOpen(!isInfoOpen);
                        return false;
                    },
                    condition: item.isLeaf,
                },
                {
                    content: <DocumentInfo item={item}/>,
                    condition: isInfoOpen,
                },
            ]
        ]
    } else if (item.trashed) {
        actions = [[
            {
                text: intl.get('item_context.restore').d('Restore'),
                Icon: RestoreFromTrashIcon,
                onClick: () => restoreFromTrashUndoable(crudServices, appState, itemId, activeWorkspace),
            },
            {
                text: intl.get('item_context.delete_forever').d('Delete Forever'),
                Icon: DeleteForeverIcon,
                onClick: () => {
                    if (activeWorkspace.confirmDeleteOpenUnder !== 'document-context-menu') {
                        activeWorkspace.setConfirmDeleteOpen('document-context-menu');
                    } else {
                        activeWorkspace.setConfirmDeleteOpen(undefined);
                    }
                    return false;
                },
            },
            {
                content: <DocumentConfirmDelete
                    item={item}
                    onCancel={() => {
                        activeWorkspace.setConfirmDeleteOpen(undefined);
                    }}
                    onSubmit={() => appState.setContextMenu(null)}
                />,
                condition: activeWorkspace.confirmDeleteOpenUnder === 'document-context-menu',
            },
        ]];
    } else if (item.archived) {
        actions = [[
            {
                text: intl.get('item_context.unarchive').d('Unarchive'),
                Icon: UnarchiveIcon,
                onClick: () => restoreFromArchiveUndoable(crudServices, appState, itemId, activeWorkspace),
            },
            {
                text: intl.get('common.delete_cap').d('Delete'),
                Icon: DeleteIcon,
                onClick: () => moveToTrashUndoable(crudServices, appState, itemId, activeWorkspace),
            },
        ]];
    } else {
        throw new Error('unsupported view');
    }

    return (
        <DocumentContextMenuComponent
            itemGroups={actions}
            handleClose={handleClose}
        />
    )
})
